/**
 * Auth constants the web app needs at RUNTIME.
 *
 * The request and response *schemas* live in `./contracts.ts` and are imported
 * by the API as `@nexus/shared/contracts`: they are eagerly built zod objects
 * that rollup cannot tree-shake, so re-exporting them from the package barrel
 * would ship every server-side rule to the browser. The barrel re-exports the
 * inferred TYPES from there, which are erased at build.
 */

/**
 * Error codes the web app branches on. Strings, not numbers, so a log line
 * reads on its own.
 *
 * `invalid_credentials` deliberately covers unknown email, wrong password and
 * a disabled account: each distinction is a fact about somebody else's account
 * and a 401 that tells them apart is a user-enumeration oracle.
 */
export const AUTH_ERROR_CODES = [
  'invalid_credentials',
  'email_not_verified',
  'email_taken',
  'invalid_token',
  'rate_limited',
  'csrf_origin_rejected',
  'unauthenticated',
  'forbidden',
  'validation_failed',
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

/** The password floor the API enforces, so a form can say so before posting. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * The sentence behind a 403 `email_not_verified`, shared with the banner.
 *
 * Signing in no longer waits for the confirmation link (with no email
 * provider configured a creator could not read it), so the address is
 * checked on the routes that move money or spend metered reads instead —
 * `POST /submissions`, `POST /wallet/payout-request`, every connection
 * mutation and the referral code (see `requireVerifiedEmail`)
 * — and the app's banner says the same
 * thing in the same words, so the refusal and the banner cannot disagree.
 */
export const EMAIL_NOT_VERIFIED_MESSAGE =
  'Confirm your email address to connect accounts, submit clips and request payouts. Check your inbox for the link, or press Resend at the top of the page.';
